<template>
  <mds-loader v-if="importDetailsLoading" />
  <content-template
    v-else
    :back="true"
    :heading="importDetails.displayedFilename"
    @back="handleBack"
  >
    <template v-slot:title>
      <heading :level="1">
        {{ importDetails.displayedFilename }}
      </heading>
    </template>
    <content-container
      title="File Details"
      class="operations-historical-detail"
    >
      <template v-slot:action>
        <mds-button
          id="download-files-action"
          variation="flat"
          icon="download"
          type="button"
          @click="openDownloadPopover"
        >
          Download
        </mds-button>
        <mds-popover
          v-model="displayDownloadPopover"
          :heading-level="5"
          position="bottom-left"
          title="Download files"
          title-hidden
          triggered-by="download-files-action"
          width="200px"
        >
          <mds-button
            v-if="importDetails.sourcefiles"
            variation="flat"
            icon="download"
            type="button"
            class="operations-historical-detail__popover-action"
            @click="downloadSourcefiles(importDetails.id)"
          >
            Source File(s) ({{ sourcefileExtension }})
          </mds-button>
          <mds-button
            v-if="!schemaFailed"
            variation="flat"
            icon="download"
            type="button"
            class="operations-historical-detail__popover-action"
            @click="downloadTransformedFile(importDetails.id)"
          >
            Transformed File (.xml)
          </mds-button>
          <mds-button
            variation="flat"
            icon="download"
            type="button"
            class="operations-historical-detail__popover-action"
            @click="downloadFileDetails"
          >
            File Details (.xlsx)
          </mds-button>
        </mds-popover>
      </template>
      <div class="operations-historical-detail__metadata">
        <span class="operations-historical-detail__metadata-item">Total Records: {{ totalRecords }}</span>
        <span class="operations-historical-detail__metadata-item">Processing Status: {{ processingStatus }}</span>
        <span class="operations-historical-detail__metadata-item">
          File Status:
          <span
            v-if="validationStatus"
            :style="`--status-color: var(--color--${validationStatus.split(' ').join('-').toLowerCase()});`"
            class="operations-historical-detail__metadata-status"
          >
            {{ validationStatus }}
          </span>
          <template v-else>
            —
          </template>
        </span>
        <span class="operations-historical-detail__metadata-item">Date: {{ uploadDate }}</span>
        <span class="operations-historical-detail__metadata-item">Processing Time: {{ processingTime }}</span>
      </div>
      <dashboard-card-group
        v-if="!schemaFailed"
        class="operations-historical-detail__overview"
      >
        <dashboard-card
          :value="totalRecordsFailed"
          header="Failed Records"
          color="var(--color--failed)"
        >
          <template v-slot:header="{ header }">
            <span class="operations-historical-detail__dashboard-header">{{ header }}</span>
          </template>
        </dashboard-card>
        <dashboard-card
          :value="totalRecordsSuccessful"
          header="Successful Records"
          color="var(--color--success)"
        >
          <template v-slot:header="{ header }">
            <span class="operations-historical-detail__dashboard-header">{{ header }}</span>
          </template>
        </dashboard-card>
      </dashboard-card-group>
      <section
        v-if="schemaFailed"
        class="operations-historical-detail__result-section"
        style="--status-color: var(--color--schema-failed);"
      >
        <heading
          :level="3"
          class="operations-historical-detail__result-section-heading"
        >
          Schema Failed
        </heading>
        <p class="operations-historical-detail__result-section-description">
          {{ importDetails.schemaErrorMessage }}
        </p>
      </section>
      <template v-else>
        <section
          v-if="failedRecordSections.length"
          class="operations-historical-detail__result-section"
          style="--status-color: var(--color--failed);"
        >
          <heading
            :level="3"
            class="operations-historical-detail__result-section-heading"
          >
            Failed Records
          </heading>
          <mds-section
            v-for="(section, index) in failedRecordSections"
            :key="index"
            :bold="true"
            :collapsible="true"
            :expanded="index === 0"
            :heading-level="4"
            :size="6"
            :title="section.title"
            class="operations-historical-detail__result-sub-section"
          >
            <mds-data-table
              :header-configs="section.headers"
              :row-data="section.paginatedData"
              :responsive="section.responsive"
              :pagination-config="tablePaginationConfig(section.data)"
              pagination="below"
              @mds-data-table-page-change="handleSectionTablePageChange(section.id, $event)"
            />
          </mds-section>
        </section>
        <section
          v-if="successfulRecordSections.length"
          class="operations-historical-detail__result-section"
          style="--status-color: var(--color--success);"
        >
          <heading
            :level="3"
            class="operations-historical-detail__result-section-heading"
          >
            Successful Records
          </heading>
          <mds-section
            v-for="(section, index) in successfulRecordSections"
            :key="index"
            :bold="true"
            :collapsible="true"
            :expanded="index === 0"
            :heading-level="4"
            :size="6"
            :title="section.title"
            class="operations-historical-detail__result-sub-section"
          >
            <mds-data-table
              :header-configs="section.headers"
              :row-data="section.paginatedData"
              :responsive="section.responsive"
              :pagination-config="tablePaginationConfig(section.data)"
              pagination="below"
              @mds-data-table-page-change="handleSectionTablePageChange(section.id, $event)"
            />
          </mds-section>
        </section>
      </template>
    </content-container>
  </content-template>
</template>

<script>
import { isEmpty } from 'lodash'
import { format, parseISO } from 'date-fns'
import { MdsButton } from '@mds/button'
import { MdsDataTable } from '@mds/data-table'
import MdsLoader from '@mds/loader'
import MdsPopover from '@mds/popover'
import MdsSection from '@mds/section'
import ContentTemplate from '../../components/ContentTemplate/ContentTemplate'
import ContentContainer from '../../components/ContentContainer/ContentContainer'
import Heading from '../../components/Heading/Heading'
import DashboardCardGroup from '../../components/DashboardCard/DashboardCardGroup'
import DashboardCard from '../../components/DashboardCard/DashboardCard'
import OperationsService from '../../services/operations-service'
import Spreadsheet from '../../../shared/services/spreadsheet-service'
import { GET_IMPORT_DETAILS, GET_IMPORT_SOURCEFILES, GET_IMPORT_TRANSFORMED_FILE } from '../../graqhql/queries'
import { DATE_FORMAT, TIMESTAMP_FORMAT, DATE_TIME_FORMAT } from '../../../shared/constants/dates'
import { VALIDATION_STATUS_COPY_MAP, SCHEMA_FAILED } from '../../../shared/constants/validation-statuses'

const PRODUCT_FUND_TABLE_HEADERS = [
  { fieldName: 'fundName', text: 'Fund Name', width: '250px' },
  { fieldName: 'fundCode', text: 'Fund Code', align: 'right', width: '100px' },
  { fieldName: 'secId', text: 'SecID', align: 'right', width: '100px' },
  { fieldName: 'cusip', text: 'CUSIP', align: 'right', width: '100px' },
  { fieldName: 'ticker', text: 'Ticker', align: 'right', width: '80px' },
  { fieldName: 'newSales', text: 'New Sales', align: 'right', width: '80px' },
  { fieldName: 'core', text: 'Core', align: 'right', width: '80px' },
  { fieldName: 'open', text: 'Open', align: 'right', width: '80px' },
  { fieldName: 'product', text: 'Product', align: 'right', width: '100px' },
  { fieldName: 'productNewSales', text: 'Prod. New Sales', align: 'right', width: '110px' },
  { fieldName: 'productCore', text: 'Prod. Core', align: 'right', width: '80px' },
  { fieldName: 'productOpen', text: 'Prod. Open', align: 'right', width: '80px' },
]

const RECORDS_PER_PROD_TABLE_HEADERS = [
  { fieldName: 'productName', text: 'Product Name' },
  { fieldName: 'records', text: 'Number of Records', align: 'right' },
]

// File detail Excel headers
const PRODUCT_FUND_EXCEL_HEADERS = PRODUCT_FUND_TABLE_HEADERS.map(({ fieldName, text }) => ({ key: fieldName, header: text }))
const RECORDS_PER_PROD_EXCEL_HEADERS = RECORDS_PER_PROD_TABLE_HEADERS.map(({ fieldName, text }) => ({ key: fieldName, header: text }))
const SUMMARY_EXCEL_HEADERS = [
  { key: 'fileType', header: 'File Type' },
  { key: 'fiduciary', header: 'Fiduciary' },
  { key: 'providerCode', header: 'Provider Code' },
  { key: 'deliveryMethod', header: 'Delivery Method' },
  { key: 'totalRecords', header: 'Total Records' },
  { key: 'failedRecords', header: 'Failed Records' },
  { key: 'successfulRecords', header: 'Successful Records' },
  { key: 'processingStatus', header: 'Processing Status' },
  { key: 'fileStatus', header: 'File Status' },
  { key: 'processingTime', header: 'Processing Time' },
  { key: 'createdDate', header: 'Record Created Date' },
  { key: 'createdBy', header: 'Record Created By' },
  { key: 'modifiedDate', header: 'Record Modified Date' },
  { key: 'modifiedBy', header: 'Record Modified By' },
]

const NUMBER_OF_ROWS_PER_PAGE_OPTIONS = [10, 20, 50]
const NUMBER_OF_ROWS_PER_PAGE_DEFAULT = 20

export default {
  components: {
    MdsButton,
    MdsDataTable,
    MdsLoader,
    MdsPopover,
    MdsSection,
    ContentTemplate,
    ContentContainer,
    Heading,
    DashboardCardGroup,
    DashboardCard,
  },
  apollo: {
    importDetails: {
      query: GET_IMPORT_DETAILS,
      variables () {
        return { id: this.$route.params.id }
      },
      update: data => data.import,
    },
  },
  data () {
    return {
      importDetails: null,
      displayDownloadPopover: false,
      sectionTablePageData: {},
    }
  },
  computed: {
    importDetailsLoading () {
      return this.$apollo.queries.importDetails.loading
    },
    sourcefileExtension () {
      if (!this.importDetails.sourcefiles) {
        return null
      }
      const extension = this.importDetails.sourcefiles[0].name.split('.').pop() // We're assuming all files will be of the same type
      return `.${extension.toLowerCase()}`
    },
    totalRecords () {
      return this.importDetails.totalRecords ?? '—'
    },
    totalRecordsFailed () {
      return this.importDetails.totalRecordsFailed ?? '—'
    },
    totalRecordsSuccessful () {
      return this.importDetails.totalRecordsSuccessful ?? '—'
    },
    processingTime () {
      return this.importDetails.processingTime || '—'
    },
    processingStatus () {
      return this.importDetails.processingStatus || '—'
    },
    uploadDate () {
      return this.importDetails.uploadDate ? format(parseISO(this.importDetails.uploadDate), DATE_FORMAT) : '—'
    },
    validationStatus () {
      return this.importDetails.validationStatus ? VALIDATION_STATUS_COPY_MAP[this.importDetails.validationStatus] : null
    },
    schemaFailed () {
      return this.importDetails.validationStatus === SCHEMA_FAILED
    },
    failedRecordSections () {
      return [
        ...(this.duplicateFundsSection ? [this.duplicateFundsSection] : []),
        ...(this.missingIdentifierFundsSection ? [this.missingIdentifierFundsSection] : []),
        ...(this.invalidProductIdentifierFundsSection ? [this.invalidProductIdentifierFundsSection] : []),
      ]
    },
    successfulRecordSections () {
      return [
        ...(this.conflictingFundsSection ? [this.conflictingFundsSection] : []),
        ...(this.unknownFundsSection ? [this.unknownFundsSection] : []),
        ...(this.successfulRecordsPerProductSection ? [this.successfulRecordsPerProductSection] : []),
      ]
    },
    conflictingFundsExist () {
      return !!this.importDetails?.conflictingFunds?.length
    },
    conflictingFundsData () {
      if (!this.conflictingFundsExist) { return null }
      return this._constructProductFundTableData(this.importDetails.conflictingFunds)
    },
    conflictingFundsSection () {
      if (!this.conflictingFundsData) { return null }
      const id = 'conflictingFunds'
      const data = this.conflictingFundsData
      const pagination = this.sectionTablePageData[id]
      return this._constructRecordSection({ id, title: 'Conflicting Funds', data, pagination, headers: PRODUCT_FUND_TABLE_HEADERS })
    },
    duplicatingFundsExist () {
      return !!this.importDetails?.duplicateFunds?.length
    },
    duplicateFundsData () {
      if (!this.duplicatingFundsExist) { return null }
      return this._constructProductFundTableData(this.importDetails.duplicateFunds)
    },
    duplicateFundsSection () {
      if (!this.duplicateFundsData) { return null }
      const id = 'duplicateFunds'
      const data = this.duplicateFundsData
      const pagination = this.sectionTablePageData[id]
      return this._constructRecordSection({ id, title: 'Duplicate Funds', data, pagination, headers: PRODUCT_FUND_TABLE_HEADERS })
    },
    missingIdentifierFundsExist () {
      return !!this.importDetails?.missingIdentifierFunds?.length
    },
    missingIdentifierFundsData () {
      if (!this.missingIdentifierFundsExist) { return null }
      return this._constructProductFundTableData(this.importDetails.missingIdentifierFunds)
    },
    missingIdentifierFundsSection () {
      if (!this.missingIdentifierFundsData) { return null }
      const id = 'missingIdentifierFunds'
      const data = this.missingIdentifierFundsData
      const pagination = this.sectionTablePageData[id]
      return this._constructRecordSection({ id, title: 'Missing Identifier Funds', data, pagination, headers: PRODUCT_FUND_TABLE_HEADERS })
    },
    invalidProductIdentifierFundsExist () {
      return !!this.importDetails?.invalidProductIdentifierFunds?.length
    },
    invalidProductIdentifierFundsData () {
      if (!this.invalidProductIdentifierFundsExist) { return null }
      return this._constructProductFundTableData(this.importDetails.invalidProductIdentifierFunds)
    },
    invalidProductIdentifierFundsSection () {
      if (!this.invalidProductIdentifierFundsData) { return null }
      const id = 'invalidProductIdentifierFunds'
      const data = this.invalidProductIdentifierFundsData
      const pagination = this.sectionTablePageData[id]
      return this._constructRecordSection({ id, title: 'Invalid Product Identifier Funds', data, pagination, headers: PRODUCT_FUND_TABLE_HEADERS })
    },
    unknownFundsExist () {
      return !!this.importDetails?.unknownFunds?.length
    },
    unknownFundsData () {
      if (!this.unknownFundsExist) { return null }
      return this._constructProductFundTableData(this.importDetails.unknownFunds)
    },
    unknownFundsSection () {
      if (!this.unknownFundsData) { return null }
      const id = 'unknownFunds'
      const data = this.unknownFundsData
      const pagination = this.sectionTablePageData[id]
      return this._constructRecordSection({ id, title: 'Unknown Funds', data, pagination, headers: PRODUCT_FUND_TABLE_HEADERS })
    },
    successfulRecordsPerProductExist () {
      return this.importDetails &&
        this.importDetails.successfulRecordsPerProduct &&
        !isEmpty(this.importDetails.successfulRecordsPerProduct)
    },
    successfulRecordsPerProductData () {
      if (!this.successfulRecordsPerProductExist) { return null }
      return this._constructRecordsPerProductTableRow(this.importDetails.successfulRecordsPerProduct)
    },
    successfulRecordsPerProductSection () {
      if (!this.successfulRecordsPerProductData) { return null }
      const id = 'successfulRecordsPerProduct'
      const data = this.successfulRecordsPerProductData
      const pagination = this.sectionTablePageData[id]
      return this._constructRecordSection({ id, title: 'Number of Records Per Product', data, pagination, headers: RECORDS_PER_PROD_TABLE_HEADERS, responsive: false })
    },
  },
  methods: {
    handleSectionTablePageChange (tableId, sectionPageData) {
      this.sectionTablePageData = Object.assign({}, this.sectionTablePageData, { [tableId]: sectionPageData })
    },
    tablePaginationConfig (tableData) {
      return {
        pageSizes: NUMBER_OF_ROWS_PER_PAGE_OPTIONS,
        pageSize: NUMBER_OF_ROWS_PER_PAGE_DEFAULT,
        totalItems: tableData.length,
        showItemsInfo: true,
        showItemsSelect: true,
      }
    },
    handleBack () {
      this.$router.push({ name: 'operations-historical' })
    },
    openDownloadPopover () {
      this.displayDownloadPopover = true
    },
    async downloadSourcefiles (id) {
      const { data } = await this.$apollo.query({
        query: GET_IMPORT_SOURCEFILES,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      OperationsService.downloadFiles(data.import.sourcefiles)
    },
    async downloadTransformedFile (id) {
      const { data } = await this.$apollo.query({
        query: GET_IMPORT_TRANSFORMED_FILE,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      const { name, url } = data.import.transformedFile
      OperationsService.downloadFile(url, name)
    },
    async downloadFileDetails (id) {
      const timestamp = format(Date.now(), TIMESTAMP_FORMAT)
      const name = `${this.importDetails.fileType || 'File'} Details Export - ${this.importDetails.displayedFilename} - ${timestamp}`
      const summaryData = this._constructFileDetailSummary(id)

      try {
        const spreadsheet = new Spreadsheet()
        spreadsheet.addSheet('Summary', SUMMARY_EXCEL_HEADERS, summaryData)
        if (this.invalidProductIdentifierFundsExist) {
          spreadsheet.addSheet('Invalid Product Identifier Funds', PRODUCT_FUND_EXCEL_HEADERS, this.invalidProductIdentifierFundsData)
        }
        if (this.missingIdentifierFundsExist) {
          spreadsheet.addSheet('Missing Identifier Funds', PRODUCT_FUND_EXCEL_HEADERS, this.missingIdentifierFundsData)
        }
        if (this.duplicatingFundsExist) {
          spreadsheet.addSheet('Duplicate Funds', PRODUCT_FUND_EXCEL_HEADERS, this.duplicateFundsData)
        }
        if (this.conflictingFundsExist) {
          spreadsheet.addSheet('Conflicting Funds', PRODUCT_FUND_EXCEL_HEADERS, this.conflictingFundsData)
        }
        if (this.unknownFundsExist) {
          spreadsheet.addSheet('Unknown Funds', PRODUCT_FUND_EXCEL_HEADERS, this.unknownFundsData)
        }
        if (this.successfulRecordsPerProductExist) {
          spreadsheet.addSheet('Number of Records Per Product', RECORDS_PER_PROD_EXCEL_HEADERS, this.successfulRecordsPerProductData)
        }
        const buffer = await spreadsheet.writeBuffer()
        this.$file.download(new Blob([buffer]), `${name}.xlsx`)
      } catch (err) {
        this.$notifications.error({ text: 'There was an issue downloading the file details. Please try again.', tinted: true })
      }
    },
    _constructRecordSection ({ id, title, data, headers, pagination = {}, responsive = true }) {
      const { firstItem, lastItem } = pagination
      const paginatedData = (firstItem && lastItem)
        ? data.slice(firstItem - 1, lastItem)
        : data.slice(0, NUMBER_OF_ROWS_PER_PAGE_DEFAULT) // firstItem and lastItem will be undefined until the MDS Data Table emits its pagination event when mounted
      return { id, title, data, headers, paginatedData, responsive }
    },
    _constructProductFundTableData (funds) {
      return funds.reduce((rows, fund, fundIndex) => {
        const { products } = fund
        const fundRows = products?.product.length
          ? products.product.map(product => this._constructProductFundTableRow({ fund, fundIndex, product }))
          : [this._constructProductFundTableRow({ fund, fundIndex })]
        return [
          ...rows,
          ...fundRows,
        ]
      }, [])
    },
    _constructProductFundTableRow ({ fund, fundIndex, product }) {
      const fundId = fund.fundCode || fund.clientFundCode
      return {
        id: product?.productId ? `fund-${fundId}-product-${product.productId}` : `fund-${fundId}`,
        fundName: fund.fundName || '—',
        fundCode: fund.clientFundCode || '—',
        secId: fund.secId || '—',
        cusip: fund.cusip || '—',
        ticker: fund.ticker || '—',
        newSales: fund.isOpenNewSales ?? '—',
        core: fund.isCore ?? '—',
        open: fund.isOpen ?? '—',
        product: product?.productId || '—',
        productNewSales: product?.isOpenNewSales ?? '—',
        productCore: product?.isCore ?? '—',
        productOpen: product?.isOpen ?? '—',
        // Used for custom "zebraing" where each fund is grouped by the same color
        class: `operations-historical-detail__table-row operations-historical-detail__table-row--${fundIndex % 2 ? 'odd' : 'even'}`,
      }
    },
    _constructRecordsPerProductTableRow (productMap) {
      return Object.entries(productMap).map(([key, value], index) => {
        return { id: index, productName: key, records: value }
      })
    },
    _constructFileDetailSummary () {
      return [
        {
          fileType: this.importDetails.fileType || '—',
          fiduciary: this.importDetails.fiduciary || '—',
          providerCode: this.importDetails.provider || '—',
          totalRecords: this.totalRecords,
          failedRecords: this.totalRecordsFailed,
          successfulRecords: this.totalRecordsSuccessful,
          processingStatus: this.processingStatus,
          fileStatus: this.validationStatus,
          processingTime: this.processingTime,
          deliveryMethod: this.importDetails.uploadMethod || '—',
          createdDate: this.importDetails.uploadDate ? format(parseISO(this.importDetails.uploadDate), DATE_TIME_FORMAT) : '—',
          createdBy: this.importDetails.uploadBy || '—',
          modifiedDate: this.importDetails.modifiedDate ? format(parseISO(this.importDetails.modifiedDate), DATE_TIME_FORMAT) : '—',
          modifiedBy: this.importDetails.modifiedBy || '—',
        },
        {}, // Skip a couple rows before displaying the schema error message.
        {},
        ['Schema Error Message'],
        [this.importDetails.schemaErrorMessage || '—'],
      ]
    },
  },
}
</script>

<style lang="scss">
@mixin status-indicator {
  display: flex;
  align-items: center;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    margin-right: $mds-space-1-x;
    border-radius: $mds-border-radius-round;
    background-color: var(--status-color);
  }
}

.operations-historical-detail {
  --color--success: #009592;
  --color--partial: #{$mds-feedback-color-warning};
  --color--failed: #{$mds-feedback-color-error};
  --color--schema-failed: #{$mds-color-violet-46};
  --color--background-primary: #{$mds-background-color-white};
  --color--background-secondary: #{$mds-background-color-light-gray};
}

.operations-historical-detail__popover-action {
  display: block;

  &:not(:last-of-type) {
    margin: 0 0 $mds-space-half-x 0;
  }
}

.operations-historical-detail__metadata {
  @include mds-body-text-l($bold: true);
  margin: 0 0 $mds-space-5-x 0;
}

.operations-historical-detail__metadata-item {
  display: inline-flex;

  &:not(:last-child) {
    padding-right: $mds-space-1-x;
    margin-right: $mds-space-1-x;
    border-right: $mds-border-separator-on-light;
  }
}

.operations-historical-detail__metadata-status {
  @include status-indicator(); // Status type set inline above
  margin-left: $mds-space-1-x;
}

.operations-historical-detail__dashboard-header {
  --status-color: var(--dashboard-card--color); // Revisit. Works, but shouldn't need to know internal custom property
  @include status-indicator();
}

.operations-historical-detail__overview {
  margin: 0 0 $mds-space-8-x 0;
}

.operations-historical-detail__result-section {
  &--failed {
    --status-color: var(--color--failed);
  }

  &--schema-failed {
    --status-color: var(--color--schema-failed);
  }

  &--successful {
    --status-color: var(--color--success);
  }

  // Revisit styles if this becomes a common pattern and move to ContentContainer
  &:not(:last-of-type) {
    padding-bottom: 80px;
  }

  &:nth-of-type(2) {
    margin: 0 -80px -80px;
    padding: 80px;
    background-color: var(--color--background-secondary);
    border-radius: 0 0 $mds-border-radius-panel $mds-border-radius-panel;

    // Flip table row background color order when displayed in the successful record section so secondary color displays first
    .operations-historical-detail__table-row--even {
      background-color: var(--color--background-secondary);
    }

    .operations-historical-detail__table-row--odd {
      background-color: var(--color--background-primary);
    }
  }
}

.operations-historical-detail__result-section-heading {
  @include status-indicator();
  @include mds-level-3-heading($bold: true);
  margin: 0 0 $mds-space-5-x 0;
}

.operations-historical-detail__result-section-description {
  margin: 0;
}

.operations-historical-detail__result-sub-section {
  &:not(:last-of-type) {
    margin: 0 0 $mds-space-4-x;
  }
}

.operations-historical-detail__table-row {
  &--even {
    background-color: var(--color--background-primary);
  }

  &--odd {
    background-color: var(--color--background-secondary);
  }
}
</style>

<style lang="scss" module>
// MDS overrides

/**
 * When there is a failed and successful record section, the successful record section
 * receives the secondary background color. This MDS override is to ensure the background color
 * of the section and table are consistent.
 */
:global(.operations-historical-detail__result-section:nth-of-type(2)) {
  .mds-data-table__responsive-wrap .mds-data-table--sticky-column {
    background-color: var(--color--background-secondary);
  }
}
</style>
